import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { PublicPageLayout } from '../components/ui/PublicPageLayout';

export const TermsOfServicePage: React.FC = () => {
  return (
    <PublicPageLayout className="py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Link
            to="/"
            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
          >
            ← Back to Home
          </Link>
        </div>

        <Card className="p-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Terms of Service</h1>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              <strong>Company:</strong> TECKVINI SOLUTIONS PRIVATE LIMITED<br/>
              <strong>Last updated:</strong> {new Date().toLocaleDateString()}
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">1. Acceptance of Terms</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  By accessing and using IntFinder services, you accept and agree to be bound by the terms 
                  and provision of this agreement. If you do not agree to abide by the above, please do not 
                  use this service.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">2. Description of Service</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  IntFinder provides data search and intelligence services through our platform. Our services 
                  include but are not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Multi-source data search capabilities</li>
                  <li>Account and identity verification services</li>
                  <li>OSINT (Open Source Intelligence) tools</li>
                  <li>Data aggregation and analysis</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">3. User Accounts</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>To access our services, you must:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate and complete registration information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Be responsible for all activities under your account</li>
                  <li>Notify us immediately of any unauthorized use</li>
                  <li>Be at least 18 years old or have parental consent</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">4. Payment Terms</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  Our services operate on a credit-based system using "Int Coins":
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Credits are purchased in advance and deducted per search</li>
                  <li>All payments are processed securely through certified payment providers</li>
                  <li>Refunds are handled on a case-by-case basis</li>
                  <li>Prices may change with 30 days notice</li>
                  <li>Unused credits do not expire but are non-transferable</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">5. Acceptable Use</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>You agree to use our services only for lawful purposes and in accordance with these terms. You may not:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Use the service for illegal activities or to violate any laws</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with or disrupt the service or servers</li>
                  <li>Use automated tools to abuse our services</li>
                  <li>Share your account credentials with others</li>
                  <li>Use the service to harass, threaten, or harm others</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">6. Data and Privacy</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  Your privacy is important to us. Please review our Privacy Policy to understand how we 
                  collect, use, and protect your information. By using our services, you consent to our 
                  data practices as described in the Privacy Policy.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">7. Service Availability</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  While we strive to maintain high service availability, we do not guarantee uninterrupted 
                  access to our services. We may:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Perform scheduled maintenance with advance notice</li>
                  <li>Experience temporary outages due to technical issues</li>
                  <li>Modify or discontinue services with reasonable notice</li>
                  <li>Limit access during high usage periods</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">8. Intellectual Property</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  All content, features, and functionality of our service are owned by IntFinder and are 
                  protected by copyright, trademark, and other intellectual property laws. You may not:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Copy, modify, or distribute our proprietary content</li>
                  <li>Reverse engineer or attempt to extract source code</li>
                  <li>Use our trademarks or branding without permission</li>
                  <li>Create derivative works based on our service</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">9. Limitation of Liability</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  To the maximum extent permitted by law, IntFinder shall not be liable for any indirect, 
                  incidental, special, consequential, or punitive damages, including but not limited to 
                  loss of profits, data, or use, arising from your use of our services.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">10. Termination</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  We may terminate or suspend your account and access to our services immediately, without 
                  prior notice, for any reason, including if you breach these terms. Upon termination:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your right to use the service will cease immediately</li>
                  <li>You remain responsible for any outstanding payments</li>
                  <li>We may delete your account and data</li>
                  <li>Certain provisions of these terms will survive termination</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">11. Changes to Terms</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  We reserve the right to modify these terms at any time. We will notify users of any 
                  material changes via email or through our service. Your continued use of the service 
                  after such modifications constitutes acceptance of the updated terms.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">12. Governing Law</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  These terms shall be governed by and construed in accordance with applicable laws. 
                  Any disputes arising from these terms or your use of our services shall be resolved 
                  through binding arbitration.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">13. Contact Information</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  If you have any questions about these terms, please contact us at:
                </p>
                <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
                  <p><strong>Email:</strong> legal@intfinder.com</p>
                  <p><strong>Support:</strong> support@intfinder.com</p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Company Information</h2>
              <div className="text-gray-700 dark:text-gray-200 space-y-4">
                <p>
                  <strong>Company Name:</strong> TECKVINI SOLUTIONS PRIVATE LIMITED<br/>
                  <strong>Product:</strong> IntFinder<br/>
                  <strong>Contact:</strong> For any terms-related questions, please contact us at support@intfinder.com
                </p>
              </div>
            </section>
          </div>
        </Card>
      </div>
    </PublicPageLayout>
  );
};
